import React, { useCallback, useEffect, useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useModalContext } from '@bcraft-frontend-lib/core'
import { PaymentFormattedLI, PaymentTypes, PaymentTypesType } from '@bcraft-frontend-lib/payments'
import block from 'bem-cn'

import { MODAL_ROUTE_URLS } from 'router/modalRoutes'

import { selectIsUserAuthenticated, selectUserData } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'

import { Icon } from 'components/UIKit'

import './PaymentModalWrapper.scss'

const b = block('payment-modal-wrapper')

type PaymentModalWrapperProps = {
  children: React.ReactChild
  path: string
  paymentType: PaymentTypesType
  system?: PaymentFormattedLI
  onBackClick: () => void
}

export const PaymentModalWrapper: React.FC<PaymentModalWrapperProps> = ({
  children,
  paymentType,
  onBackClick,
  path,
  system,
}) => {
  const { closeModal, replaceLastModal } = useModalContext()

  const user = useSelector(selectUserData, shallowEqual)
  const isAuth = useSelector(selectIsUserAuthenticated, shallowEqual)
  const { profile, payment } = useSelector(selectLocaleDict, shallowEqual)

  const title = useMemo(() => {
    if (system && onBackClick) {
      return (
        <div className={b('back-block')}>
          <Icon type='Caret_Down' className={b('arrow')} onClick={onBackClick} />
          <span>
            {payment[paymentType]}
          </span>
          <span>/</span>
          <span>{system.name}</span>
        </div>
      )
    }

    return <div className={b('title')}>{payment.chooseMethod}</div>
  }, [payment, onBackClick, paymentType, system])

  const needCloseModal = useMemo(() => !isAuth || !user?.isSelfRegistered, [isAuth, user?.isSelfRegistered])

  const onCloseModal = useCallback(() => {
    closeModal(path)()
  }, [closeModal, path])

  useEffect(() => {
    if (needCloseModal) {
      onCloseModal()
    }
  }, [isAuth])

  if (needCloseModal) return null
  return (
    <div className={b()}>
      <div className={b('header')}>
        {profile.payment}
        <button onClick={closeModal(path)} type="button" className={b('header-close')}>
          <div className={b('header-close-icon')}>×</div>
        </button>
      </div>
      {!system?.slug ? (
        <>
          <div className={b('divider')} />
          <div className={b('navigation')}>
            <div
              className={b('navigation-item', { isActive: paymentType === PaymentTypes.DEPOSIT })}
              onClick={replaceLastModal(MODAL_ROUTE_URLS.DEPOSIT())}>
              {payment.deposit}
            </div>
            <div
              className={b('navigation-item', { isActive: paymentType === PaymentTypes.PAYOUT })}
              onClick={replaceLastModal(MODAL_ROUTE_URLS.WITHDRAWAL())}>
              {payment.withdraw}
            </div>
          </div>
          <div className={b('divider')} />
        </>
      ) : (
        title
      )}
      <div className={b('container')}>
        {children}
      </div>
    </div>
  )
}
